import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, Lock, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public landing page an invited teammate hits from the email link:
 * /accept-invite?token=XXX
 *
 * Flow:
 *  1. Validate the token against GET /auth/invite/{token} (shows invitee + company).
 *  2. Collect a password + confirmation.
 *  3. POST /auth/invite/accept, persist the returned JWT, redirect to the dashboard.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("No invite token in this link.");
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/auth/invite/${encodeURIComponent(token)}`)
      .then((r) => setInvite(r.data))
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "This invite link is no longer valid.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/auth/invite/accept`, { token, password });
      localStorage.setItem("app_token", r.data.token);
      localStorage.setItem("app_user", JSON.stringify(r.data.user));
      toast.success(`Welcome, ${r.data.user.name || r.data.user.username}!`);
      // Full reload so AuthContext re-hydrates with the new token.
      window.location.href = "/";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not activate your account");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500" data-testid="invite-loading">
        Checking your invite...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-rose-200 bg-white p-6 shadow-sm" data-testid="invite-error">
          <div className="flex items-center gap-2 text-rose-700 font-semibold">
            <ShieldCheck size={18} /> Invite not valid
          </div>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <p className="mt-4 text-xs text-slate-500">
            Ask your admin to send you a fresh invite link, then open it in the same browser.
          </p>
          <div className="mt-4">
            <Link to="/login" className="text-sm text-blue-700 hover:underline">
              Already have an account? Sign in →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isReset = invite?.kind === "reset";

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md" data-testid="invite-page">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-slate-900">
            <ShieldCheck size={20} className="text-blue-600" />
            <h1 className="text-xl font-semibold">
              {isReset ? "Reset your password" : "Welcome aboard"}
            </h1>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {isReset
              ? "Pick a new password to regain access."
              : `You've been invited to join ${invite.company_name || "the team"}. Pick a password to activate your account.`}
          </p>

          <div className="mt-4 space-y-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
            <div className="flex items-center gap-2 text-slate-700">
              <Mail size={14} className="text-slate-400" />
              <span>{invite.email}</span>
            </div>
            <div className="text-xs text-slate-500">
              Signing in as <span className="font-mono">{invite.username}</span>
            </div>
          </div>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="password">New password</Label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  minLength={6}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="pl-8"
                  data-testid="invite-password"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                minLength={6}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your password"
                data-testid="invite-confirm"
              />
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="invite-submit"
            >
              {saving ? "Activating..." : isReset ? "Set new password" : "Activate my account"}
            </Button>
          </form>
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">
          Already set up? <Link to="/login" className="text-blue-700 hover:underline">Sign in instead</Link>
        </p>
      </div>
    </div>
  );
}
